import React, { useState } from 'react';
import ReactSelect from 'react-select';
import styled from 'styled-components';
import { X, CheckCircle, Loader } from 'lucide-react';
import { showSuccessToast, showErrorToast } from '../../../../atoms/Utlis/Toast';
import BrandDatePicker from '../../../../atoms/Utlis/BrandDatePicker';
import { recordPaymentAPI } from '../../../../../Redux/ApiCalls/Dashboard/InvoiceAPI';
import type { RecordPaymentPayload, PaymentMethod, InvoiceInstallment } from '../../../../../Data/invoice.dto';
import { PAYMENT_METHOD_LABELS } from '../../../../../Data/invoice.dto';

interface PaymentRecordModalProps {
  invoiceId: number;
  installment?: InvoiceInstallment | null;
  defaultAmount?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const today = () => new Date().toISOString().split('T')[0];

const PaymentRecordModal: React.FC<PaymentRecordModalProps> = ({
  invoiceId,
  installment,
  defaultAmount = '',
  onClose,
  onSuccess,
}) => {
  const [amount, setAmount] = useState(installment?.amount || defaultAmount);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [paymentDate, setPaymentDate] = useState(today());
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      showErrorToast('Please enter a valid amount.');
      return;
    }
    const payload: RecordPaymentPayload = {
      amount,
      method,
      payment_date: paymentDate,
      reference: reference || undefined,
      notes: notes || undefined,
      installment_id: installment?.id,
    };
    try {
      setLoading(true);
      await recordPaymentAPI(invoiceId, payload);
      showSuccessToast('Payment recorded successfully ✅');
      onSuccess();
      onClose();
    } catch (err: any) {
      showErrorToast(err?.message || 'Failed to record payment.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Record Payment</ModalTitle>
          <CloseBtn type="button" onClick={onClose}><X size={18} /></CloseBtn>
        </ModalHeader>

        {installment && (
          <InstallmentInfo>
            <strong>{installment.label}</strong> — {installment.percent}%
            {installment.amount && ` (₹${installment.amount})`}
          </InstallmentInfo>
        )}

        <Form onSubmit={handleSubmit}>
          <FormRow>
            <FormGroup>
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={amount}
                onChange={e => {
                  const val = e.target.value;
                  if (val === '') {
                    setAmount('');
                  } else {
                    const parsed = parseFloat(val);
                    setAmount(isNaN(parsed) ? '' : String(Math.max(0, parsed)));
                  }
                }}
                required
                placeholder="0.00"
              />
            </FormGroup>
            <FormGroup>
              <Label>Payment Date *</Label>
              <BrandDatePicker
                value={paymentDate}
                onChange={(v) => setPaymentDate(v)}
                required
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label>Payment Method *</Label>
            <Select value={method} onChange={e => setMethod(e.target.value as PaymentMethod)}>
              {Object.entries(PAYMENT_METHOD_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Reference / Transaction ID</Label>
            <Input
              type="text"
              value={reference}
              onChange={e => setReference(e.target.value)}
              placeholder="Optional"
            />
          </FormGroup>

          <FormGroup>
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional notes"
            />
          </FormGroup>

          <ButtonRow>
            <CancelBtn type="button" onClick={onClose}>Cancel</CancelBtn>
            <SubmitBtn type="submit" disabled={loading}>
              {loading ? <Loader size={16} className="spin" /> : <CheckCircle size={16} />}
              {loading ? 'Recording…' : 'Record Payment'}
            </SubmitBtn>
          </ButtonRow>
        </Form>
      </Modal>
    </Overlay>
  );
};

export default PaymentRecordModal;

// ─── Styled Components ────────────────────────────────────────

const gradient = 'linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%)';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.48);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 28px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  font-family: Urbanist, sans-serif;
  background: ${gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  color: #171717;
`;

const InstallmentInfo = styled.div`
  padding: 10px 14px;
  border-radius: 10px;
  background: rgba(201, 77, 170, 0.07);
  border: 1px solid rgba(201, 77, 170, 0.15);
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #c94daa;
  margin-bottom: 18px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 12px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const Label = styled.label`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.65);
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid rgba(23, 23, 23, 0.15);
  border-radius: 10px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  color: #171717;
  outline: none;
  background: #fafafa;

  &:focus {
    border-color: #c94daa;
    background: #fff;
  }
`;

type SelectProps = {
  value: string;
  onChange: (e: { target: { value: string } }) => void;
  children: React.ReactNode;
};

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: '44px',
    borderRadius: '10px',
    borderColor: state.isFocused ? 'rgba(201, 77, 170, 0.4)' : 'rgba(23, 23, 23, 0.15)',
    background: state.isFocused ? '#ffffff' : '#fafafa',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(201, 77, 170, 0.1)' : 'none',
    fontFamily: 'Urbanist, sans-serif',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
    '&:hover': {
      borderColor: 'rgba(201, 77, 170, 0.32)',
      background: '#ffffff',
    },
  }),
  valueContainer: (base: any) => ({ ...base, padding: '0 12px' }),
  input: (base: any) => ({ ...base, margin: 0, padding: 0 }),
  singleValue: (base: any) => ({ ...base, color: '#171717', fontWeight: 600 }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    color: state.isFocused ? '#c94daa' : 'rgba(23, 23, 23, 0.45)',
    padding: '0 12px 0 8px',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.15s ease, color 0.15s ease',
  }),
  menu: (base: any) => ({
    ...base,
    marginTop: '8px',
    borderRadius: '14px',
    padding: '6px',
    border: '1px solid rgba(201, 77, 170, 0.12)',
    boxShadow: '0 18px 36px rgba(23, 23, 23, 0.14)',
    overflow: 'hidden',
    zIndex: 9999,
  }),
  menuList: (base: any) => ({ ...base, padding: 0 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  option: (base: any, state: any) => ({
    ...base,
    borderRadius: '10px',
    padding: '10px 12px',
    fontFamily: 'Urbanist, sans-serif',
    fontSize: '14px',
    fontWeight: state.isSelected ? 700 : 600,
    color: '#171717',
    background: state.isSelected
      ? 'linear-gradient(135deg, rgba(255, 201, 81, 0.18), rgba(201, 77, 170, 0.16))'
      : state.isFocused
        ? 'rgba(201, 77, 170, 0.08)'
        : '#ffffff',
  }),
};

const Select: React.FC<SelectProps> = ({ value, onChange, children }) => {
  const options = React.Children.toArray(children)
    .filter((child): child is React.ReactElement<any> => React.isValidElement<any>(child) && child.type === 'option')
    .map((child) => ({ value: String(child.props.value), label: child.props.children }));

  const selectedOption = options.find((option) => option.value === String(value)) ?? null;

  return (
    <ReactSelect
      value={selectedOption}
      onChange={(option) => onChange({ target: { value: option?.value ?? '' } })}
      options={options}
      styles={selectStyles}
      menuPosition="fixed"
      menuPortalTarget={document.body}
      isSearchable={false}
    />
  );
};

const Textarea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid rgba(23, 23, 23, 0.15);
  border-radius: 10px;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  color: #171717;
  background: #fafafa;
  resize: vertical;
  outline: none;

  &:focus {
    border-color: #c94daa;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
`;

const CancelBtn = styled.button`
  padding: 10px 20px;
  border: 1px solid rgba(23, 23, 23, 0.15);
  border-radius: 10px;
  background: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.72);
  cursor: pointer;
`;

const SubmitBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 10px 22px;
  border: none;
  border-radius: 10px;
  background: ${gradient};
  color: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(201, 77, 170, 0.25);
  transition: opacity 0.15s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spin {
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
